import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ManualEntryModal from "../../work-hours/components/ManualEntryModal";
import HoursApprovalPanel from "../components/HoursApprovalPanel";
import { buildThemeClasses } from "../theme";

// ---------------------------------------------------------------------------
// Horas por aprobar (#1346) — sección propia de Nómina (`/payroll/hours-approval`).
//
// Antes la tabla vivía encima de «Autorización de incidencias», en la misma
// página, y solo la veía quien aprobaba. Ahora es donde:
//
//   • cada colaborador REGISTRA sus horas, con el mismo formulario manual de
//     Horas Trabajadas (`ManualEntryModal`: una sola copia, no una bifurcación),
//     y sigue el estado de sus solicitudes (pendiente, aprobada, rechazada y por
//     qué);
//   • los tech leads y los dueños ven el detalle de cada solicitud y la
//     aprueban o rechazan con motivo.
//
// La regla (#1281, pareja desde #1346): toda entrada manual de más de 1 h espera
// aprobación —también las de un dueño o un tech lead— y nadie aprueba lo suyo.
// Esta pantalla solo pinta: el servidor decide qué filas manda y qué se puede
// decidir sobre cada una.
// ---------------------------------------------------------------------------

export default function HoursApprovalSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Cambia tras registrar: la tabla vuelve a preguntar al servidor.
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="h-full w-full overflow-y-auto p-6" data-testid="hours-approval-section">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-hourglass-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{t("payroll.menu.hoursApproval")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>{t("payroll.hoursApprovalPage.subtitle")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setAdding(true);
            }}
            data-testid="hours-approval-add"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
          >
            <i className="ri-add-line" />
            {t("payroll.hoursApprovalPage.add")}
          </button>
        </div>

        {notice && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-600" data-testid="hours-approval-notice">
            <i className="ri-checkbox-circle-line mr-1" />
            {notice}
          </div>
        )}

        <HoursApprovalPanel refreshKey={refreshKey} />
      </div>

      {adding && (
        <ManualEntryModal
          isDarkMode={isDarkMode}
          onClose={() => setAdding(false)}
          onSaved={(result) => {
            setAdding(false);
            // Si quedó esperando aprobación, decirlo: todavía no cuenta.
            setNotice(t(result?.approval === "pending" ? "payroll.hoursApprovalPage.savedPending" : "payroll.hoursApprovalPage.saved"));
            setRefreshKey((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}
